import map from "lodash/map";
import { register } from "@rill/loader";
import appendTypeAndCountry from "../../utils/append-type-and-country";

register(
  {
    name: "admin_allUsers",
    meta: ["admin_users"],
  },
  async (
    { api, momentTZ },
    { page, pageSize, name, email, type, _id, organizationId }
  ) => {
    if (type === "all" && !name && !email && !_id && !organizationId) return [];
    const query = makeQuery({
      page,
      pageSize,
      name,
      email,
      type,
      momentTZ,
      _id,
      organizationId,
    });

    const users = await (
      await api("user", {
        query,
      })
    ).json();
    return map(users, (user) => appendTypeAndCountry(user, momentTZ));
  }
);

/**
 * Returns users count
 */
register(
  {
    name: "admin_allUsersCount",
    meta: ["admin_users"],
  },
  async (
    { api, momentTZ },
    { page, pageSize, name, email, type, _id, organizationId }
  ) => {
    if (type === "all" && !name && !email && !_id && !organizationId) return 0;
    const query = makeQuery({
      page,
      pageSize,
      name,
      email,
      type,
      momentTZ,
      _id,
      organizationId,
    });
    delete query.skip;
    query.limit = false;

    const res = await (await api("users-count", { query })).json();
    return res.count;
  }
);

register(
  {
    name: "admin_profileUser",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api, momentTZ }, { user }) => {
    if (!user) return;
    try {
      const res = await api("user-info", {
        query: {
          _id: user,
          customFields: { exerciseLibrary: 0 },
        },
      });
      const data = await res.json();
      const { actualUser: profileUser = {} } = data;
      return appendTypeAndCountry(profileUser, momentTZ);
    } catch (_) {}
  }
);

/**
 * Utility that generates query object to be used to fetch from the database.
 * Taking account of pagination and dynamic filters
 * @param  {Number} [page] - the page number to fetch. Defaults to 1
 * @param  {Number} [pageSize] - the page size to fetch. Defaults to 100
 * @param  {String} [name] - search term
 * @param  {String} [type] - used to filter users into categories mainly based on permissions and subscription. Currently: ['admin', 'ambassador', 'paid', 'trial']
 * @return {Object} [query] - to be used to query the database
 */
function makeQuery({
  page = 1,
  pageSize = 100,
  name,
  email,
  type,
  momentTZ,
  _id,
  organizationId,
}) {
  const query = {
    limit: pageSize,
    skip: (page - 1) * pageSize,
    sort: { name: 1 },
  };

  query["stripe.plan"] = {
    expired: { $regex: "[^(launch_freeTrial)|(free_trial)]" },
    paid: { $regex: "[^(launch_freeTrial)|(free_trial)]" },
    trial: { $regex: "(launch_freeTrial)|(free_trial)" },
  }[type];

  query["stripe.period_end"] = {
    paid: { $gte: momentTZ().toDate() },
    expired: { $lt: momentTZ().toDate() },
  }[type];

  if (name) {
    query["name"] = { $regex: "^.*" + name + ".*$", $options: "i" };
  }
  if (email) {
    query["email"] = { $regex: "^.*" + email + ".*$", $options: "i" };
  }
  if (_id) {
    query["_id"] = _id;
  }
  if (organizationId) {
    query["organizationId"] = organizationId;
  }
  if (["admin", "ambassador"].includes(type)) {
    query["permissions"] = { $elemMatch: { $regex: `${type}` } };
  }
  return query;
}
